if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientId = this.dataset.recipientId || this.dataset.sectionId;
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.recipientId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.recipientId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.recipientId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.recipientId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.recipientId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.recipientId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.recipientId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.recipientId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        if (this.sendonInput) {
          this.updateSendDateMin();
          this.sendonInput.addEventListener('focus', this.updateSendDateMin.bind(this));
          this.sendonInput.addEventListener('input', this.onSendDateInput.bind(this));
          this.sendonInput.addEventListener('change', this.onSendDateChange.bind(this));
        }

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.emailInput.required = true;
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.emailInput.required = false;
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
        this.sendonInput.setCustomValidity('');
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      localToday() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      updateSendDateMin() {
        this.sendonInput.min = this.localToday();
      }

      isPastSendDate() {
        return Boolean(this.sendonInput.value && this.sendonInput.value < this.sendonInput.min);
      }

      sendDateErrorMessage() {
        this.sendonInput.setCustomValidity('');
        return this.sendonInput.validationMessage || `Please select ${this.sendonInput.min} or a later date.`;
      }

      onSendDateInput() {
        this.sendonInput.setCustomValidity('');
        this.clearErrorMessage();
      }

      onSendDateChange() {
        this.updateSendDateMin();
        if (!this.isPastSendDate()) return;

        const message = this.sendDateErrorMessage();
        this.sendonInput.value = '';
        this.sendonInput.setCustomValidity(message);
        this.displayErrorMessage(this.defaultErrorHeader, { send_on: [message] });
        this.sendonInput.reportValidity();
        this.sendonInput.focus();
      }

      validate() {
        if (!this.checkboxInput.checked) return true;

        this.clearErrorMessage();
        this.updateSendDateMin();

        if (this.isPastSendDate()) {
          this.sendonInput.setCustomValidity(this.sendDateErrorMessage());
        } else {
          this.sendonInput.setCustomValidity('');
        }

        const invalidInput = [this.emailInput, this.sendonInput].find((input) => !input.checkValidity());
        if (!invalidInput) return true;

        const key = invalidInput === this.emailInput ? 'email' : 'send_on';
        this.displayErrorMessage(this.defaultErrorHeader, { [key]: [invalidInput.validationMessage] });
        invalidInput.reportValidity();
        invalidInput.focus();
        return false;
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.recipientId}`;
            const fieldSelector = `#Recipient-${key}-${this.recipientId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}
